import {useCallback, useEffect, useMemo, useRef} from 'react';

import {cn} from '../../helpers/cn';

const DEFAULT_LENGTH = 6;
const CELL_WIDTH = 48;
const CELL_GAP = 8;

export type OtpInputSize = 'sm' | 'md';

export type OtpInputProps = {
  length?: number;
  size?: OtpInputSize;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  className?: string;
};

function toDigits(value: string, length: number): string[] {
  return Array.from({length}, (_, index) => value[index] ?? '');
}

function fromDigits(digits: string[]): string {
  return digits.join('');
}

function isDigit(value: string): boolean {
  return /^\d$/.test(value);
}

function getMaxWidth(length: number): number {
  return length * CELL_WIDTH + (length - 1) * CELL_GAP;
}

export function OtpInput({
  length = DEFAULT_LENGTH,
  size = 'sm',
  value = '',
  onChange,
  onComplete,
  disabled = false,
  error = false,
  autoFocus = true,
  className
}: OtpInputProps) {
  const digits = useMemo(() => toDigits(value, length), [value, length]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const completedValueRef = useRef('');

  const emitChange = useCallback(
    (nextDigits: string[]) => {
      const nextValue = fromDigits(nextDigits);
      onChange?.(nextValue);

      if (
        nextValue.length === length &&
        /^\d+$/.test(nextValue) &&
        nextValue !== completedValueRef.current
      ) {
        completedValueRef.current = nextValue;
        onComplete?.(nextValue);
      }

      if (nextValue.length < length) {
        completedValueRef.current = '';
      }
    },
    [length, onChange, onComplete]
  );

  const focusInput = (index: number) => {
    const target = inputRefs.current[index];
    target?.focus();
    target?.select();
  };

  useEffect(() => {
    if (autoFocus && !disabled) {
      focusInput(0);
    }
  }, [autoFocus, disabled, length]);

  const applyDigits = (nextDigits: string[], focusIndex?: number) => {
    emitChange(nextDigits);
    if (typeof focusIndex === 'number') {
      window.requestAnimationFrame(() => focusInput(focusIndex));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const raw = event.target.value.replace(/\D/g, '');

    if (!raw) {
      const nextDigits = [...digits];
      nextDigits[index] = '';
      applyDigits(nextDigits, index);
      return;
    }

    if (raw.length > 1) {
      const pasted = raw.slice(0, length);
      const nextDigits = toDigits(pasted, length);
      applyDigits(nextDigits, Math.min(pasted.length, length - 1));
      return;
    }

    if (!isDigit(raw)) {
      return;
    }

    const nextDigits = [...digits];
    nextDigits[index] = raw;
    applyDigits(nextDigits, index < length - 1 ? index + 1 : index);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const nextDigits = [...digits];

      if (digits[index]) {
        nextDigits[index] = '';
        applyDigits(nextDigits, index);
        return;
      }

      if (index > 0) {
        nextDigits[index - 1] = '';
        applyDigits(nextDigits, index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) {
      return;
    }

    const nextDigits = [...digits];
    pasted.split('').forEach((char, offset) => {
      if (index + offset < length) {
        nextDigits[index + offset] = char;
      }
    });

    const nextFilledIndex = Math.min(index + pasted.length, length - 1);
    applyDigits(nextDigits, nextFilledIndex);
  };

  return (
    <div
      className={cn(
        'ds-otp-input',
        size === 'md' && 'ds-otp-input--md',
        error && 'ds-otp-input--error',
        className
      )}
      style={{
        gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))`,
        maxWidth: getMaxWidth(length)
      }}
      role="group"
      aria-label="ورود کد یک‌بارمصرف"
    >
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          value={digit}
          disabled={disabled}
          aria-label={`رقم ${index + 1}`}
          aria-invalid={error || undefined}
          maxLength={1}
          tabIndex={index + 1}
          onChange={(event) => handleChange(event, index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          onPaste={(event) => handlePaste(event, index)}
          onFocus={(event) => event.currentTarget.select()}
          className="ds-otp-input__cell"
        />
      ))}
    </div>
  );
}
